// Side-effect import: fires `GET /auth/config` during module evaluation, so the
// request that gates the whole app overlaps evaluation of everything below
// instead of starting after React mounts. Keep this import first.
import './auth/bootPrefetch';
// Configure the generated API client (baseUrl, auth interceptors) BEFORE the
// router import below — `./router` eagerly evaluates every route module via
// routeTree.gen, and generated query options capture the client baseUrl at
// creation time. Keep this import first.
import './api/generatedClient';
import './api/generatedAgentsClient';
import React, { lazy, Suspense, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { AuthRouterBoundary } from './auth/AuthRouterBoundary';
import { RuntimeAuthProvider } from './auth/runtimeAuth';
import { useAuth } from './hooks/useAuth';
import { router } from './router';
import {
  registerSessionExpiryHandler,
  retryUnlessSessionExpired,
} from './auth/sessionExpiry';
import { API_BASE } from './config';
import { logger } from './lib/logger';
import {
  applyTheme,
  readThemePreference,
  subscribeSystemTheme,
} from './lib/theme';
import { BootScreen } from './components/BootScreen';
import { IdentityErrorBanner } from './components/IdentityErrorBanner';
import { TooltipProvider } from './components/ui/tooltip';
import { reloadForNewBuild } from './utils/chunkLoadError';
import { initPostHog } from './lib/posthogClient';
// Side-effect import: initializes i18next synchronously (bundled resources) and
// applies the saved/browser language before first render.
import './i18n';
import { setDebugConsole } from '@/lib/logger';
import './index.css';

// react-grab: element inspector for dev servers and PR previews. A dynamic
// import inside the same guard as the devtools shell below, so the expression
// folds to `false` in every other build and the whole import is dropped rather
// than shipped. Placed AFTER the static imports because that is where it
// actually runs — static imports hoist above any statement, so writing it at
// the top of the file would only misrepresent the order without changing it.
if (import.meta.env.DEV || import.meta.env.MODE === 'pr-preview') {
  import('react-grab');
}

// `?debugViewer` turns the structured logger into a console logger for this
// tab: every level accepted, every record mirrored. The flag is read HERE, at
// the app edge, because `@/lib/logger` is a dependency-free leaf that touches
// no browser global — see `setDebugConsole`. Without it a built preview drops
// `logger.debug` under `MIN_RANK` and mirrors nothing, which is how a viewer
// question ends up debugged with a stray `console.*` instead (ENG-2404).
if (
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('debugViewer')
) {
  setDebugConsole(true);
}

// PostHog before anything renders, so exception autocapture's global handlers
// cover auth-SDK hydration and the BootScreen, not just what runs after the
// router mounts. What it still cannot see is an error thrown while the imports
// above evaluate — module evaluation finishes before this line runs.
initPostHog();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: retryUnlessSessionExpired },
    // Preserve TanStack's default mutation behavior explicitly: mutations are
    // never replayed by query policy. Auth transport owns its one safe replay.
    mutations: { retry: false },
  },
});

// Devtools ship in exactly two places — every dev server (`DEV`) and the
// per-PR preview build (`--mode pr-preview`, see .env.pr-preview) — and this
// expression is what enforces it: both env reads are literals in a
// `vite build`, so everywhere else (dev/preprod/prod deploys, the hosted demo)
// it folds to `null` and the dynamic import — with the three devtools packages
// behind it — is dropped from the bundle rather than shipped as an unreachable
// chunk. `@tanstack/react-devtools` and friends are devDependencies on that
// basis; preview builds install with dev deps, so the lazy chunk still builds.
const AppDevtools =
  import.meta.env.DEV || import.meta.env.MODE === 'pr-preview'
    ? lazy(() =>
        import('./components/AppDevtools').then((m) => ({
          default: m.AppDevtools,
        })),
      )
    : null;

function InnerApp() {
  const auth = useAuth();

  useEffect(() => registerSessionExpiryHandler(auth.logout), [auth.logout]);

  // Route guards read `context.auth` when a match loads; TanStack re-reads the
  // context object on every render but never re-runs `beforeLoad` for matches
  // that already resolved. Without this, a verdict reached while auth was
  // unsettled sticks — which is how a live session could end up parked on
  // /login. Re-evaluate the current matches whenever auth reaches a new
  // verdict. The first settle is SDK hydration, which is also the router's own
  // first mount (`AuthRouterBoundary` holds it back until then), so it needs no
  // invalidate.
  //
  // Canonical identity landing is NOT a verdict change any more (ENG-1316) and
  // fires no invalidate: no guard reads `user`. A future guard that does needs
  // its own trigger here.
  const hasSettled = useRef(false);
  useEffect(() => {
    if (auth.isLoading) return;
    if (!hasSettled.current) {
      hasSettled.current = true;
      return;
    }
    void router.invalidate();
  }, [auth.isLoading, auth.isAuthenticated]);

  return (
    <AuthRouterBoundary
      isLoading={auth.isLoading}
      loadingFallback={<BootScreen />}
    >
      {/* Default delay for tooltips that consume the shared provider.
          SimpleTooltip wraps each instance in its own provider so the
          mesh-settings form's "tooltips chase the cursor" issue is
          handled there — this root provider is for direct Tooltip
          consumers (assistant sidebar, menus, etc.) that benefit from
          the shared skip-delay optimization. */}
      <TooltipProvider delayDuration={500}>
        <RouterProvider router={router} context={{ auth, queryClient }} />
      </TooltipProvider>
      {/* `!user` and not just `auth.error`: TanStack keeps the last successful
          `data` when a refetch fails, so a flaky window-focus refetch leaves
          `error` set while identity is still fully usable. Bannering that would
          pin a dismiss-less bar over the header of a working app. `useAuth`
          logs the suppressed case. With `user` null, `isIdentityPending` is the
          accurate in-flight signal for the retry. */}
      {auth.error && !auth.user && (
        <IdentityErrorBanner
          onRetry={auth.retry}
          onLogout={() => void auth.logout()}
          retrying={auth.isIdentityPending}
        />
      )}
      {AppDevtools && (
        <Suspense fallback={null}>
          <AppDevtools />
        </Suspense>
      )}
    </AuthRouterBoundary>
  );
}

// Apply saved theme before first render to avoid flash. `system` follows
// the OS and the subscription keeps `.dark` in sync if it flips later.
applyTheme(readThemePreference());
subscribeSystemTheme();

logger.info('[Config] API base resolved', {
  apiBase: API_BASE,
  hostname: window.location.hostname,
});
logger.info('[App] Frontend started.');

// Vite fires `vite:preloadError` on `window` when a dynamic import 404s
// — almost always because the user has a stale tab open after a redeploy
// and the hashed chunk filename in their loaded JS no longer exists on
// the CDN. Auto-reload pulls the new bundle (which has the new hashes)
// and recovers without user intervention. Sentinel guard prevents an
// infinite reload loop in the rare case the new build also fails the
// preload (e.g. CDN partial deploy) — we let the in-app error boundary
// catch it on the second pass and surface the manual Reload CTA.
const PRELOAD_RELOAD_KEY = 'khorium-preload-reload-at';
window.addEventListener('vite:preloadError', (event) => {
  logger.warn('[App] vite:preloadError — stale bundle, reloading.', {
    error: (event as { payload?: Error }).payload,
  });
  const last = sessionStorage.getItem(PRELOAD_RELOAD_KEY);
  const nowMs = Date.now();
  if (last && nowMs - Number(last) < 10_000) {
    // We just reloaded — don't loop. Let the error bubble to the
    // route-level boundary, which renders StaleBundleNotice.
    return;
  }
  sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(nowMs));
  // Cache-bust the document fetch — a plain reload would re-serve the same
  // edge-cached (old-hash) index.html and 404 the same chunk again. (PostHog
  // flushes its own buffer via the SDK's pagehide beacon.)
  reloadForNewBuild();
});

async function bootstrap() {
  // Inlined rather than `isDemoMode()` so the bundler can fold this to a
  // constant. Vite replaces `import.meta.env.VITE_MODE` with a literal, but it
  // does not propagate that through a function call — `isDemoMode` minifies to
  // `()=>!1` and the `if` around it survives, which keeps the dynamic import
  // reachable and emits the whole mock deck (plus ~13.5 MB of fixture assets)
  // into every production build. Written out here, the branch is dead code and
  // Rollup drops the chunk. Verified by `vite build --mode production`.
  if (import.meta.env.VITE_MODE === 'demo') {
    // E2E seams (`window.__khorium*`), behind the same literal as the worker.
    (await import('./mocks/demoHooks')).installDemoHooks();
    try {
      const { worker } = await import('./mocks/browser');
      await worker.start({ onUnhandledRequest: 'bypass' });
    } catch (err) {
      // Demo-only path: a failed SW registration must be loud, not a blank page.
      logger.error('[demo] MSW worker failed to start — API calls will 404', {
        error: err,
      });
    }
  }
  const rootEl = document.getElementById('root');
  if (rootEl) {
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <RuntimeAuthProvider>
            <InnerApp />
          </RuntimeAuthProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
}
void bootstrap();
